// Snapshot → CanonicalRoom: how a jam room births itself from the seed it is
// named for. The inverse of shared/publish-bridge.ts (which renders a room
// back into a Snapshot for publishing).
//
// Until now a room started empty and only ever held what a client proposed
// into it — the first joiner imported the seed into Live and re-published it,
// and two first joiners racing produced doubled lanes. Hydration makes the
// room reconcile against its own seed instead: the plan below is folded into
// canonical state by the Durable Object before the first client ever sees
// the room.
//
// What hydrates and what doesn't:
//   • tracks: name/mute/solo carried; `arm` stays false (a per-musician
//     stance, never room truth — see canonical.ts); volume/pan carried
//     (clamped by the fold, same as any accepted op).
//   • clips: the 13 ClipData scalars, mapped 1:1 (duration not endTime,
//     warpMode.value not the {value,name} object, numeric color). A clip
//     whose audioKey is null/absent or whose object is gone from R2 is
//     SKIPPED, not hydrated audio-less — a receiver cannot materialise a
//     clip with no audio source, and a clip the client can never build
//     would read as permanent divergence on every digest pass.
//   • song extras (grid, scale, cue points, groups, take lanes): dropped —
//     the room has no vocabulary for them. Publish carries them back over
//     from the origin arrangement by track NAME, which hydration preserves,
//     so the round trip keeps them.
//
// Not hydratable at all (returns null): no arrangement, or an arrangement in
// which no clip anywhere carries an audioKey (stems-only and pre-arrangement
// seeds). Those rooms keep the legacy behavior — the first joiner seeds them.
//
// Pure: no I/O. The caller supplies id minting and the R2 existence check.

import { warpRenderHash } from "./clip-render.js";
import { isInstrumentId } from "../instruments.js";
import { MAX_NOTES_PER_CLIP } from "../midi/notes.js";
import type { AudioClipInfo, MidiClipInfo, SessionAudioClipInfo, Snapshot } from "../seed-history.js";
import { wireFileName, type ClipData } from "./protocol.js";
import { seedClipGeometry } from "../playback/geometry.js";
import { applyAcceptedOps, cloneDevices, emptyRoom, type AcceptedOp, type CanonicalRoom, validScale } from "./state.js";

/** Why hydration left a clip out. */
export type HydrationSkipReason =
  /** An audio clip whose seed carries no `audioKey`: no object was ever
   * uploaded for it. Session View clips of a seed made before the extension
   * shipped slot-clip audio (2026-10-08, #66) all read this way; so does a
   * slot clip the uploader could not bounce or read. Nothing on the server
   * can repair it — the audio never left the producer's machine. */
  | "no-audio"
  /** The seed names an object that is gone from R2 — a verify that never
   * completed, or a pool object collected from under the seed. */
  | "missing-object"
  /** Unusable geometry (non-finite start, non-positive duration) or a MIDI
   * clip without a usable notes payload. */
  | "unusable";

export interface HydrationSkippedClip {
  /** The lane's name as the seed captured it. */
  track: string;
  name: string;
  why: HydrationSkipReason;
  /** The slot index of a Session View clip; absent for a timeline clip. */
  slot?: number;
}

/** What hydration left out, by cause — the number that reaches Workers Logs
 * and /inspect, so "skipped N clips" says whether anything was lost. */
export interface HydrationSkipped {
  noAudio: number;
  missingObject: number;
  unusable: number;
  /** Distinct keys the seed names that R2 does not hold. */
  missingKeys: string[];
  /** The clips themselves, first `HYDRATION_SKIP_DETAIL_MAX` only. */
  clips: HydrationSkippedClip[];
  /** Set when the room hydrated before this record existed and the summary
   * was reconstructed afterwards from its seed (JamRoom `/inspect`): a keyed
   * seed clip counts as skipped only when the room lacks it AND R2 lacks it. */
  backfilled?: true;
}

/** How many skipped clips `HydrationSkipped.clips` names in full. */
export const HYDRATION_SKIP_DETAIL_MAX = 50;

export interface HydrationPlan {
  /** The room's birth, expressed as ordinary accepted ops (all rev 1) so the
   * DO can persist them through the same path as any accepted batch. */
  ops: AcceptedOp[];
  /** The ops folded into a room: roomRev 1, tempoRev 1, every entity rev 1. */
  room: CanonicalRoom;
  /** Clips dropped for missing or absent audio (or unusable geometry). */
  skippedClips: number;
  /** The same clips, by cause. */
  skipped: HydrationSkipped;
}

/** Every distinct audioKey the snapshot references — what the caller should
 * check against R2 before planning. */
export function distinctAudioKeys(snapshot: Snapshot): string[] {
  const keys = new Set<string>();
  for (const track of snapshot.audioTracks) {
    for (const clip of [...track.arrangementClips, ...(track.sessionClips ?? [])]) {
      if (clip.audioKey) keys.add(clip.audioKey);
      if (clip.render?.key) keys.add(clip.render.key);
    }
  }
  for (const track of snapshot.midiTracks ?? []) {
    for (const clip of [...track.arrangementClips, ...(track.sessionClips ?? [])]) {
      if (clip.audioKey) keys.add(clip.audioKey);
    }
  }
  return [...keys];
}

/** A MIDI clip hydrates iff it carries a usable notes payload. */
function hydratableMidiClip(clip: MidiClipInfo): boolean {
  return Array.isArray(clip.notes) && clip.notes.length <= MAX_NOTES_PER_CLIP && !clip.notesOmitted;
}

/** Whether the arrangement holds anything a room can be born from: audio
 * clips with objects, or MIDI clips with notes. */
export function hasHydratableContent(snapshot: Snapshot): boolean {
  if (distinctAudioKeys(snapshot).length > 0) return true;
  return (snapshot.midiTracks ?? []).some(
    (t) => t.arrangementClips.some(hydratableMidiClip) || (t.sessionClips ?? []).some(hydratableMidiClip),
  );
}

/**
 * Plan a room's canonical state from its seed's arrangement. Returns null when
 * the seed is not hydratable (no snapshot, or no clip carries an audioKey).
 *
 * `newId` mints entity ids (the DO passes its own t_/c_ uuid minting);
 * `audioExists` answers whether a clip's R2 object is actually present.
 */
export function planHydration(
  snapshot: Snapshot | null,
  newId: (kind: "track" | "clip") => string,
  audioExists: (key: string) => boolean,
): HydrationPlan | null {
  if (!snapshot) return null;
  if (!hasHydratableContent(snapshot)) return null;

  const ops: AcceptedOp[] = [];
  let skippedClips = 0;
  const skipped: HydrationSkipped = { noAudio: 0, missingObject: 0, unusable: 0, missingKeys: [], clips: [] };
  const missingKeys = new Set<string>();
  const skip = (track: string, clip: { name: string; slot?: number }, why: HydrationSkipReason): void => {
    skippedClips++;
    if (why === "no-audio") skipped.noAudio++;
    else if (why === "missing-object") skipped.missingObject++;
    else skipped.unusable++;
    if (skipped.clips.length < HYDRATION_SKIP_DETAIL_MAX) {
      skipped.clips.push({ track, name: clip.name, why, ...(clip.slot !== undefined ? { slot: clip.slot } : {}) });
    }
  };
  const audioCount = snapshot.audioTracks.length;

  for (const [ord, track] of snapshot.audioTracks.entries()) {
    const trackId = newId("track");
    ops.push({ kind: "trackCreated", id: trackId, ord, name: track.name, rev: 1 });
    // trackCreated defaults every scalar; assert the captured values so the
    // fold (and the persisted room) carries them. `arm` stays the default.
    ops.push({ kind: "trackFieldSet", id: trackId, field: "mute", value: track.mute, rev: 1 });
    ops.push({ kind: "trackFieldSet", id: trackId, field: "solo", value: track.solo, rev: 1 });
    ops.push({ kind: "trackFieldSet", id: trackId, field: "volume", value: track.volume, rev: 1 });
    ops.push({ kind: "trackFieldSet", id: trackId, field: "pan", value: track.panning, rev: 1 });
    if (track.sends && track.sends.length > 0) ops.push({ kind: "trackSendsSet", id: trackId, sends: [...track.sends], rev: 1 });
    if (track.devices && track.devices.length > 0) ops.push({ kind: "trackDevicesSet", id: trackId, devices: cloneDevices(track.devices), rev: 1 });

    // Timeline clips, then Session View clips (slot set, startTime 0).
    const audioClips: Array<AudioClipInfo & Partial<Pick<SessionAudioClipInfo, "slot" | "warpMarkers">>> = [
      ...track.arrangementClips,
      ...(track.sessionClips ?? []),
    ];
    for (const clip of audioClips) {
      if (!clip.audioKey) {
        skip(track.name, clip, "no-audio");
        continue;
      }
      if (!audioExists(clip.audioKey)) {
        missingKeys.add(clip.audioKey);
        skip(track.name, clip, "missing-object");
        continue;
      }
      const session = clip.slot !== undefined;
      const startTime = session ? 0 : clip.startTime;
      if (!Number.isFinite(startTime) || !Number.isFinite(clip.duration) || clip.duration <= 0) {
        skip(track.name, clip, "unusable");
        continue;
      }
      // What the object means — a region render played flat, a source file
      // through its markers, a warped clip on the Set's grid — is the one
      // rule in shared/playback/geometry.ts, the same the seed page and the
      // clip library read, so a room never plays a clip differently from
      // the seed it was opened from.
      const { warpMode, sourceTempo, warpMarkers, transpose, detune, warpSettings, ...markers } = seedClipGeometry(clip, snapshot.song.tempo);
      const data: ClipData = {
        // The seed's snapshot holds the uploader's absolute path; the room
        // keeps only the name (finding 12).
        filePath: wireFileName(clip.filePath),
        startTime,
        duration: clip.duration,
        ...markers,
        name: clip.name,
        color: clip.color,
        muted: clip.muted,
        warpModeValue: warpMode,
        ...(session ? { slot: clip.slot } : {}),
        // Where this placement came from in a producer's clip library.
        ...(clip.libraryClip && typeof clip.libraryClip.id === "string" && typeof clip.libraryClip.ownerId === "string"
          ? { libraryClip: { id: clip.libraryClip.id, ownerId: clip.libraryClip.ownerId } }
          : {}),
        ...(warpMarkers ? { warpMarkers } : {}),
        ...(sourceTempo !== undefined ? { sourceTempo } : {}),
        ...(transpose !== undefined ? { transpose } : {}),
        ...(detune !== undefined ? { detune } : {}),
        ...(warpSettings !== undefined ? { warpSettings } : {}),
      };
      // The seed's render of the warp, ready again: made from exactly this
      // clip (the hash is of the clip as hydrated), so a Live joiner of the
      // remix builds from it without waiting on a browser.
      if (clip.render && audioExists(clip.render.key)) {
        data.render = { key: clip.render.key, hash: warpRenderHash({ ...data, audioKey: clip.audioKey }), tempo: clip.render.tempo };
      }
      ops.push({
        kind: "clipUpserted",
        id: newId("clip"),
        trackId,
        clip: data,
        audioKey: clip.audioKey,
        rev: 1,
      });
    }
  }

  // MIDI lanes follow the audio lanes in ord — the same audio-first order the
  // client's index space uses, so a hydrated room and a fresh join agree.
  for (const [i, track] of (snapshot.midiTracks ?? []).entries()) {
    const trackId = newId("track");
    ops.push({
      kind: "trackCreated",
      id: trackId,
      ord: audioCount + i,
      name: track.name,
      rev: 1,
      trackKind: "midi",
      ...(isInstrumentId(track.instrument) ? { instrument: track.instrument } : {}),
    });
    ops.push({ kind: "trackFieldSet", id: trackId, field: "mute", value: track.mute, rev: 1 });
    ops.push({ kind: "trackFieldSet", id: trackId, field: "solo", value: track.solo, rev: 1 });
    ops.push({ kind: "trackFieldSet", id: trackId, field: "volume", value: track.volume, rev: 1 });
    ops.push({ kind: "trackFieldSet", id: trackId, field: "pan", value: track.panning, rev: 1 });
    if (track.sends && track.sends.length > 0) ops.push({ kind: "trackSendsSet", id: trackId, sends: [...track.sends], rev: 1 });
    if (track.devices && track.devices.length > 0) ops.push({ kind: "trackDevicesSet", id: trackId, devices: cloneDevices(track.devices), rev: 1 });

    const midiClips: Array<MidiClipInfo & { slot?: number }> = [
      ...track.arrangementClips,
      ...(track.sessionClips ?? []),
    ];
    for (const clip of midiClips) {
      if (!hydratableMidiClip(clip)) {
        skip(track.name, clip, "unusable");
        continue;
      }
      const session = clip.slot !== undefined;
      const startTime = session ? 0 : clip.startTime;
      if (!Number.isFinite(startTime) || !Number.isFinite(clip.duration) || clip.duration <= 0) {
        skip(track.name, clip, "unusable");
        continue;
      }
      // The bounce is optional and must actually exist to be referenced.
      const audioKey = clip.audioKey && audioExists(clip.audioKey) ? clip.audioKey : null;
      const data: ClipData = {
        filePath: "",
        startTime,
        duration: clip.duration,
        isWarped: false,
        looping: false,
        startMarker: 0,
        endMarker: clip.duration,
        loopStart: 0,
        loopEnd: clip.duration,
        name: clip.name,
        color: clip.color,
        muted: clip.muted,
        warpModeValue: 0,
        clipKind: "midi",
        notes: clip.notes,
        notesDigest: clip.notesDigest,
        ...(session ? { slot: clip.slot } : {}),
      };
      ops.push({ kind: "clipUpserted", id: newId("clip"), trackId, clip: data, audioKey, rev: 1 });
    }
  }

  // tempoRev 1, deliberately: this IS the seed's asserted tempo, so readers
  // that guard against the emptyRoom(120) placeholder (publish's tempo
  // fallback) must treat it as real. First: the clips above are measured at it,
  // and an unwarped clip's beats follow any tempo that lands after it (state.ts).
  const tempo = snapshot.song.tempo;
  if (Number.isFinite(tempo) && tempo > 0) {
    ops.unshift({ kind: "tempoSet", value: tempo, rev: 1 });
  }

  // Scene names, where the seed recorded them (full-mode snapshots do; lite
  // ones don't) — rev 1 like the tempo: the seed's own, not a placeholder.
  // An unnamed scene in Live reads "" — that is "no name known", null on
  // the wire, so a receiver never writes it over a Set's own name.
  const sceneNames = (snapshot.song.scenes ?? []).map((sc) => (sc.name === "" ? null : sc.name));
  if (sceneNames.length > 0) ops.push({ kind: "sceneNamesSet", names: sceneNames, rev: 1 });

  // Locators: the seed's cue points, rev 1 — asserted, so a receiver
  // reconciles to them (a rev-0 list is "nobody said anything").
  const cuePoints = (snapshot.song.cuePoints ?? [])
    .filter((c) => Number.isFinite(c.time) && c.time >= 0)
    .map((c) => ({ name: c.name, time: c.time }))
    .sort((a, b) => a.time - b.time);
  if (cuePoints.length > 0) ops.push({ kind: "cuePointsSet", points: cuePoints, rev: 1 });

  // The master fader, when the seed recorded it — rev 1: the seed's own.
  const master = snapshot.song.master;
  if (master && Number.isFinite(master.volume) && Number.isFinite(master.panning)) {
    ops.push({ kind: "masterSet", volume: master.volume, pan: master.panning, rev: 1 });
  }

  // The Set's scale, when the seed recorded one — rev 1 (Live 12's scale
  // mode on, or a named scale with degrees; an empty interval list is a
  // seed captured before the read existed and says nothing).
  const sc = snapshot.song.scale;
  if (sc && Array.isArray(sc.intervals) && sc.intervals.length > 0) {
    const scale = validScale({ rootNote: sc.rootNote, name: sc.name, mode: sc.mode, intervals: sc.intervals });
    if (scale) ops.push({ kind: "scaleSet", scale, rev: 1 });
  }

  // The Set's return tracks, when the seed recorded them — rev 1.
  const returns = (snapshot.song.returns ?? []).map((r) => ({ name: r.name, mute: r.mute, solo: r.solo, volume: r.volume, pan: r.panning }));
  if (returns.length > 0) ops.push({ kind: "returnsSet", returns, rev: 1 });
  // The master's and the returns' device chains, when recorded — rev 1.
  if (snapshot.song.masterDevices) ops.push({ kind: "chainSet", owner: "master", devices: cloneDevices(snapshot.song.masterDevices), rev: 1 });
  for (const [i, r] of (snapshot.song.returns ?? []).entries()) {
    if (r.devices) ops.push({ kind: "chainSet", owner: `return:${i}`, devices: cloneDevices(r.devices), rev: 1 });
  }

  const room = emptyRoom();
  applyAcceptedOps(room, ops);
  room.roomRev = 1;
  skipped.missingKeys = [...missingKeys];
  return { ops, room, skippedClips, skipped };
}
